import type {ConferenceApiModel} from '../../../api/conference';
import type {
  CoreClientConferencesListParams,
  CoreCoachConferencesListParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {apiRequest} from '../../../api/utils/apiRequest';
import {isIamCoach} from '../../../utils/entity/isIamCoach';
import {expandResponse} from '../utils/expandResponse';

export {conferenceFetchList};

type ClientConfig = BaseConfig<CoreClientConferencesListParams> & {
  fetchAllConferences?: boolean;
};

type CoachConfig = BaseConfig<CoreCoachConferencesListParams> & {
  fetchAllConferences?: boolean;
  fetchMyClientConferences?: boolean;
};

type Config = {
  asClient: ClientConfig;
  asCoach: CoachConfig;
};

const strategy: AsyncStrategy = async (roles, config) => {
  return isIamCoach() ? roles.coach(config) : roles.client(config);
};

async function conferenceFetchList(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

conferenceFetchList.asClient = asClient;
conferenceFetchList.asCoach = asCoach;

async function asClient(config: Config): Promise<ConferenceApiModel[]> {
  const allConferences: ConferenceApiModel[] = [];

  let currentParams = config.asClient.params as CoreClientConferencesListParams;

  while (true) {
    const response = await apiRequest<CoreClientConferencesListParams>({
      method: 'client.conferences.list',
      options: config.asClient.options,
      params: currentParams,
    });

    const conferences = expandResponse(response, config.asClient);

    allConferences.push(...conferences);

    if (config.asClient.fetchAllConferences && response._next) {
      currentParams = {
        ...currentParams,
        cursor: response._next,
      };
    } else {
      break;
    }
  }

  return allConferences;
}

async function asCoach(config: Config): Promise<ConferenceApiModel[]> {
  let myClientConferences: ConferenceApiModel[] = [];

  if (
    config.asCoach?.fetchMyClientConferences === undefined ||
    config.asCoach.fetchMyClientConferences
  ) {
    myClientConferences = await asClient(config);
  }

  const allConferences: ConferenceApiModel[] = [];

  let currentParams = config.asCoach.params as CoreCoachConferencesListParams;

  while (true) {
    const response = await apiRequest<CoreCoachConferencesListParams>({
      method: 'coach.conferences.list',
      options: config.asCoach.options,
      params: currentParams,
    });

    const conferences = expandResponse(response, config.asCoach);

    allConferences.push(...conferences);

    if (config.asCoach.fetchAllConferences && response._next) {
      currentParams = {
        ...currentParams,
        cursor: response._next,
      };
    } else {
      break;
    }
  }

  return [...allConferences, ...myClientConferences];
}
